import { weightedLength } from "@ondit/publishing";
import type { DistributionSource, PublishingPlatform } from "~/core/api/distribution";
import { type Locale, translate } from "~/core/i18n";

export function articleMarkdown(source: DistributionSource): string {
	const body = source.blocks
		.map((block) => {
			switch (block.type) {
				case "answer":
					return block.text;
				case "section":
					return `## ${block.heading}\n\n${block.md}`;
				case "faq":
					return block.items.map((item) => `### ${item.q}\n\n${item.a}`).join("\n\n");
				case "table":
					return [
						block.caption ?? "",
						...block.rows.map((row) => row.map((cell, i) => `${block.columns[i]}: ${cell}`).join(" · ")),
					].join("\n\n");
				case "stat":
					return `${block.label}: ${block.value}${block.unit ? ` ${block.unit}` : ""}${block.source_url ? ` (${block.source_url})` : ""}`;
				case "sources":
					return block.items.map((item) => `- [${item.label}](${item.url})`).join("\n");
				case "chart":
					return `${block.title}\n\n${JSON.stringify(block.series)}`;
			}
			return "";
		})
		.join("\n\n");
	return `# ${source.title}\n\n${body}\n\n${sourceLine(source)}`;
}

/**
 * The citation every post carries, in the article's language rather than the
 * reader's: it is part of the text that gets pasted into the platform.
 */
function sourceLine(source: DistributionSource): string {
	return `${translate(source.locale as Locale, "distribution.originalArticle")}: ${source.canonical}`;
}

export function adaptArticle(source: DistributionSource, platform: PublishingPlatform): string {
	if (platform.format === "article") return articleMarkdown(source);
	const answer = source.blocks.find((b) => b.type === "answer");
	const paragraphs = [source.title, answer?.type === "answer" ? answer.text : ""];
	if (platform.format === "video") {
		const headings = source.blocks.filter((b) => b.type === "section").map((b) => `${b.heading}\n\n${b.md}`);
		return [
			translate(source.locale as Locale, "distribution.narrationDraft"),
			...paragraphs,
			...headings.map((h, i) => `${i + 1}. ${h}`),
			sourceLine(source),
		]
			.filter(Boolean)
			.join("\n\n");
	}
	// Keep a complete sourced answer; length warnings are preferable to silently
	// cutting a qualification out of a factual statement.
	return [...paragraphs, sourceLine(source)].filter(Boolean).join("\n\n");
}

export function distributionLength(body: string, platform: PublishingPlatform): number {
	return weightedLength(body, platform.id === "x");
}
